import { useState } from 'react'
import {
  ROTULO_TIPO_CAMPO,
  TIPOS_CAMPO_DISPONIVEIS,
  precisaDeOpcoes,
  type CampoDto,
  type EntidadeCampo,
  type TipoCampo,
} from '@crm/shared'
import { ErroRequisicao } from '../api/cliente'
import { useCriarCampo } from '../api/hooks-leads'
import { formatarMoeda } from '../formato'
import { CLASSE_DO_CAMPO } from './pagina'
import { Aviso, Botao, Campo, Modal, juntar } from './ui'

/**
 * Os campos customizados na tela, num lugar só: a janela de campo novo, a
 * entrada de cada tipo no formulário e o texto que vai na coluna da lista.
 * Servem ao contato e, desde a ADR 0069, à empresa: a Configuração, a ficha
 * de cada um e as listas de Contatos e de Empresas usam as mesmas peças.
 */

/** Vira `nome_do_campo` a partir do que a pessoa digitou. */
function sugerirChave(nome: string): string {
  return (
    nome
      .normalize('NFD')
      // remove os acentos que o NFD separou da letra
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .replace(/^(\d)/, 'c$1')
      .slice(0, 60)
  )
}

/**
 * O formulário do campo novo. Na Configuração ele é "Novo campo"; nas listas
 * de Contatos e de Empresas é "Nova coluna", porque cada campo vira uma coluna.
 */
export function ModalNovoCampo({
  aberto,
  entidade,
  proximaOrdem,
  aoFechar,
  comoColuna = false,
}: {
  aberto: boolean
  entidade: EntidadeCampo
  proximaOrdem: number
  aoFechar: () => void
  comoColuna?: boolean
}) {
  const criar = useCriarCampo()
  const [nome, setNome] = useState('')
  const [chave, setChave] = useState('')
  const [chaveTocada, setChaveTocada] = useState(false)
  const [tipo, setTipo] = useState<TipoCampo>('texto')
  const [obrigatorio, setObrigatorio] = useState(false)
  const [opcoes, setOpcoes] = useState('')

  const erro = criar.error instanceof ErroRequisicao ? criar.error : null
  const ficha = entidade === 'empresa' ? 'empresa' : 'contato'

  function fechar() {
    setNome('')
    setChave('')
    setChaveTocada(false)
    setTipo('texto')
    setObrigatorio(false)
    setOpcoes('')
    criar.reset()
    aoFechar()
  }

  function salvar() {
    criar.mutate(
      {
        nome,
        chave,
        tipo,
        entidade,
        obrigatorio,
        ordem: proximaOrdem,
        opcoes: precisaDeOpcoes(tipo)
          ? opcoes
              .split(';')
              .map((o) => o.trim())
              .filter(Boolean)
          : undefined,
      },
      { onSuccess: fechar },
    )
  }

  return (
    <Modal
      aberto={aberto}
      titulo={comoColuna ? 'Nova coluna' : `Novo campo de ${ficha}`}
      aoFechar={fechar}
      fecharAoClicarFora={false}
      rodape={
        <>
          <Botao onClick={fechar}>Cancelar</Botao>
          <Botao variante="primario" carregando={criar.isPending} onClick={salvar}>
            {comoColuna ? 'Criar coluna' : 'Criar campo'}
          </Botao>
        </>
      }
    >
      <div className="space-y-3">
        {comoColuna ? (
          <p className="text-sm text-tinta-500">
            A coluna é um campo da ficha de cada {ficha}: aparece aqui na lista e para preencher na
            ficha. Ela também fica em Configuração, em Campos.
          </p>
        ) : null}
        {erro && !erro.campo('nome') && !erro.campo('chave') && !erro.campo('opcoes') ? (
          <Aviso tipo="erro">{erro.corpo.mensagem}</Aviso>
        ) : null}
        <Campo
          rotulo="Nome"
          value={nome}
          autoFocus
          erro={erro?.campo('nome')}
          onChange={(e) => {
            setNome(e.target.value)
            if (!chaveTocada) setChave(sugerirChave(e.target.value))
          }}
          placeholder={entidade === 'empresa' ? 'Quantidade de alunos' : 'Série do aluno'}
        />
        <Campo
          rotulo="Chave"
          value={chave}
          erro={erro?.campo('chave')}
          onChange={(e) => {
            setChaveTocada(true)
            setChave(e.target.value)
          }}
          dica="Nome interno do campo: só letras minúsculas, números e o sinal _ . É o título da coluna na planilha de importação."
        />
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium text-tinta-600">Tipo</span>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoCampo)}
            className={juntar(CLASSE_DO_CAMPO, 'w-full')}
          >
            {TIPOS_CAMPO_DISPONIVEIS.map((t) => (
              <option key={t} value={t}>
                {ROTULO_TIPO_CAMPO[t]}
              </option>
            ))}
          </select>
        </label>
        {precisaDeOpcoes(tipo) ? (
          <Campo
            rotulo="Opções"
            value={opcoes}
            erro={erro?.campo('opcoes')}
            onChange={(e) => setOpcoes(e.target.value)}
            dica="Separe por ponto e vírgula."
            placeholder="Infantil; Fundamental; Médio"
          />
        ) : null}
        <label className="flex items-center gap-2 text-sm text-tinta-700">
          <input
            type="checkbox"
            checked={obrigatorio}
            onChange={(e) => setObrigatorio(e.target.checked)}
          />
          <span>Obrigatório no cadastro de {ficha}</span>
        </label>
      </div>
    </Modal>
  )
}

/** A entrada de um campo no formulário da ficha, pelo tipo dele. */
export function CampoCustomizado({
  campo,
  valor,
  erro,
  aoMudar,
}: {
  campo: CampoDto
  valor: string
  erro?: string
  aoMudar: (valor: string) => void
}) {
  const rotulo = campo.obrigatorio ? `${campo.nome} *` : campo.nome

  if (campo.tipo === 'checkbox') {
    return (
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={valor === 'true'}
          onChange={(e) => aoMudar(e.target.checked ? 'true' : 'false')}
        />
        <span>{rotulo}</span>
      </label>
    )
  }

  if (campo.tipo === 'lista') {
    return (
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-tinta-800">{rotulo}</span>
        <select
          value={valor}
          onChange={(e) => aoMudar(e.target.value)}
          className="w-full rounded-md ring-1 ring-tinta-200 bg-superficie px-3 py-2 text-sm"
        >
          <option value="">-</option>
          {(campo.opcoes ?? []).map((opcao) => (
            <option key={opcao} value={opcao}>
              {opcao}
            </option>
          ))}
        </select>
        {erro ? <span className="mt-1 block text-xs text-red-600">{erro}</span> : null}
      </label>
    )
  }

  if (campo.tipo === 'multipla_escolha') {
    const marcadas = valor
      .split(';')
      .map((v) => v.trim())
      .filter(Boolean)
    return (
      <div>
        <span className="mb-1 block text-sm font-medium text-tinta-800">{rotulo}</span>
        <div className="flex flex-wrap gap-3">
          {(campo.opcoes ?? []).map((opcao) => (
            <label key={opcao} className="flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                checked={marcadas.includes(opcao)}
                onChange={(e) => {
                  const novas = e.target.checked
                    ? [...marcadas, opcao]
                    : marcadas.filter((m) => m !== opcao)
                  aoMudar(novas.join('; '))
                }}
              />
              <span>{opcao}</span>
            </label>
          ))}
        </div>
        {erro ? <span className="mt-1 block text-xs text-red-600">{erro}</span> : null}
      </div>
    )
  }

  return (
    <Campo
      rotulo={rotulo}
      type={campo.tipo === 'data' ? 'date' : campo.tipo === 'numero' ? 'number' : 'text'}
      value={valor}
      erro={erro}
      onChange={(e) => aoMudar(e.target.value)}
    />
  )
}

/** O que está gravado na ficha, como texto para o formulário. */
export function valoresParaFormulario(campos: Record<string, unknown> | undefined): Record<string, string> {
  return Object.fromEntries(
    Object.entries(campos ?? {}).map(([chave, valorBruto]) => [
      chave,
      Array.isArray(valorBruto) ? valorBruto.join('; ') : String(valorBruto ?? ''),
    ]),
  )
}

/** O formulário de volta para o servidor: só os preenchidos, e a múltipla escolha como lista. */
export function montarCampos(
  definicoes: CampoDto[],
  valores: Record<string, string>,
): Record<string, unknown> {
  const saida: Record<string, unknown> = {}
  for (const campo of definicoes) {
    if (!campo.ativo) continue
    const bruto = valores[campo.chave]
    if (bruto === undefined || bruto === '') continue
    saida[campo.chave] =
      campo.tipo === 'multipla_escolha'
        ? bruto
            .split(';')
            .map((v) => v.trim())
            .filter(Boolean)
        : bruto
  }
  return saida
}

/**
 * O valor do campo como texto curto, para a coluna da lista. `null` quando
 * não há valor, para a célula mostrar o traço de vazio.
 */
export function textoDoCampo(campo: CampoDto, valor: unknown): string | null {
  if (valor === undefined || valor === null || valor === '') return null
  if (Array.isArray(valor)) return valor.length > 0 ? valor.join(', ') : null
  switch (campo.tipo) {
    case 'checkbox':
      return valor === true || valor === 'true' ? 'Sim' : 'Não'
    case 'moeda':
      return formatarMoeda(Number(valor))
    case 'numero':
      return Number(valor).toLocaleString('pt-BR')
    case 'data': {
      // Gravada como AAAA-MM-DD, sem hora: montar a data no fuso daria o dia anterior.
      const [ano, mes, dia] = String(valor).split('-')
      return ano && mes && dia ? `${dia}/${mes}/${ano}` : String(valor)
    }
    default:
      return String(valor)
  }
}
